import { z } from 'zod'

export const scopeTypeSchema = z.enum(['area', 'length', 'count'])
export type ScopeType = z.infer<typeof scopeTypeSchema>

export const scopeMeasurementSchema = z.object({
  num: z.number().nonnegative(),
  unit: z.string(),
})

export const methodAssignmentSchema = z.object({
  id: z.string(),
  techniqueId: z.string(),
  procedure: z.string(),
  instructions: z.string(),
  estimatedScope: scopeMeasurementSchema.optional(),
})
export type MethodAssignment = z.infer<typeof methodAssignmentSchema>

export const methodPlanningSchema = z.object({
  requireEstimate: z.boolean(),
  requiredFields: z.array(z.enum(['procedure', 'instructions'])),
})
export type MethodPlanning = z.infer<typeof methodPlanningSchema>

export const techniqueCatalogueSchema = z
  .object({
    degradationTypes: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        techniqueIds: z.array(z.string()),
      })
    ),
    techniques: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        scopeType: scopeTypeSchema.optional(),
        description: z.string(),
        pod: z.number().min(0).max(1).nullable(),
        procedure: z.string().optional(),
        instructions: z.string().optional(),
      })
    ),
    units: z.object({
      area: z.string(),
      length: z.string(),
      count: z.string(),
    }),
  })
  .superRefine((catalogue, context) => {
    const ids = catalogue.techniques.map((technique) => technique.id)
    const degradationIds = catalogue.degradationTypes.map((type) => type.id)
    if (
      new Set(ids).size !== ids.length ||
      new Set(degradationIds).size !== degradationIds.length
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Catalogue IDs must be unique',
      })
    if (
      catalogue.degradationTypes.some((type) =>
        type.techniqueIds.some((id) => !ids.includes(id))
      )
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Degradation types must reference configured techniques',
      })
  })

export type TechniqueCatalogue = z.infer<typeof techniqueCatalogueSchema>
export type InspectionTechnique = TechniqueCatalogue['techniques'][number]
